import type { Locale, RouteKind } from "../content/types";

const LOCALE_STORAGE_KEY = "prismark-locale";

export const locales: readonly Locale[] = ["en", "zh", "zh-Hant"];

// 每种语言 × 每种页面的路径。所有按语言拼路径的地方都查这张表，不要再写 `locale === "zh" ? … : …`：
// 三目在加语言后会静默把新语言送进某个旧分支，而 Record<Locale, …> 少一项会编译失败。
export const localizedPaths: Record<RouteKind, Record<Locale, string>> = {
  landing: { en: "/", zh: "/zh", "zh-Hant": "/zh-Hant" },
  privacy: { en: "/privacy", zh: "/zh/privacy", "zh-Hant": "/zh-Hant/privacy" },
  support: { en: "/support", zh: "/zh/support", "zh-Hant": "/zh-Hant/support" },
  terms: { en: "/terms", zh: "/zh/terms", "zh-Hant": "/zh-Hant/terms" }
};

/** <html lang>，预渲染与客户端 PageMeta 共用，避免加载后跳变。 */
export const htmlLangByLocale: Record<Locale, string> = { en: "en", zh: "zh-CN", "zh-Hant": "zh-Hant-TW" };

/** <link rel="alternate" hreflang>。 */
export const hreflangByLocale: Record<Locale, string> = { en: "en", zh: "zh-Hans", "zh-Hant": "zh-Hant" };

export const ogLocaleByLocale: Record<Locale, string> = { en: "en_US", zh: "zh_CN", "zh-Hant": "zh_TW" };

/** 语言切换器里的显示名，每种语言都用自己的文字写。 */
export const localeLabels: Record<Locale, string> = { en: "English", zh: "简体中文", "zh-Hant": "繁體中文" };

/**
 * 把一个浏览器语言标签映射到站点语言；不认识的返回 null。
 * zh-TW / zh-HK / zh-MO / zh-Hant* 走繁体，其余 zh*（含 zh-Hans-HK 这类显式简体）走简体。
 */
export function localeFromLanguageTag(language: string): Locale | null {
  const tag = language.trim().toLowerCase();
  if (/^zh-(hant|tw|hk|mo)(-|$)/.test(tag)) return "zh-Hant";
  if (tag === "zh" || tag.startsWith("zh-")) return "zh";
  if (tag === "en" || tag.startsWith("en-")) return "en";
  return null;
}

export function getSavedLocale(): Locale | null {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    const match = locales.find((locale) => locale === saved);
    if (match) return match;
  } catch {}
  return null;
}

export function saveLocale(locale: Locale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {}
}

export function getBrowserLocale(languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages): Locale {
  for (const language of languages) {
    const locale = localeFromLanguageTag(language);
    if (locale) return locale;
  }

  return "en";
}

export function getBrowserLandingRedirect(browserLocale: Locale = getBrowserLocale()) {
  if (getSavedLocale() !== null) return null;
  // 英文就是 / 本身，不用跳
  return browserLocale === "en" ? null : localizedPaths.landing[browserLocale];
}
